import React from "react";
import { Link, useParams } from "react-router-dom";
import { Overline, Section } from "../components/UI";
import { SEO } from "../components/SEO";
import { buildCanonical } from "../lib/siteOrigin";
import { ArrowLeft } from "lucide-react";
import { APPROVED_CLINICAL_INSIGHTS } from "../data/approvedClinicalInsights";
import { LowPlateletsPregnancyTrend } from "./clinical-insights/LowPlateletsPregnancyTrend";
import { SevereHeadachePregnancyMigraineRedFlags } from "./clinical-insights/SevereHeadachePregnancyMigraineRedFlags";
import { PolyhydramniosNormalDiabetesTest } from "./clinical-insights/PolyhydramniosNormalDiabetesTest";
import { RecurrentUrineInfectionPregnancy } from "./clinical-insights/RecurrentUrineInfectionPregnancy";
import { AntenatalSteroidsFetalLungMaturity } from "./clinical-insights/AntenatalSteroidsFetalLungMaturity";

// Registry of Clinical Insight body components, keyed by slug.
// Every approved Insight must have an entry here.
const INSIGHT_BODIES = {
  "low-platelets-pregnancy-trend": LowPlateletsPregnancyTrend,
  "severe-headache-pregnancy-migraine-red-flags": SevereHeadachePregnancyMigraineRedFlags,
  "polyhydramnios-normal-diabetes-test": PolyhydramniosNormalDiabetesTest,
  "recurrent-urine-infection-pregnancy": RecurrentUrineInfectionPregnancy,
  "antenatal-steroids-fetal-lung-maturity": AntenatalSteroidsFetalLungMaturity,
};

export function ClinicalInsightsHub() {
  return (
    <Section>
      <SEO
        title="Clinical Insights | Dr Tania Gurdip Singh"
        description="Short, decision-point clinical insights on high-risk pregnancy, authored by Dr Tania Gurdip Singh."
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Clinical Insights", item: "/clinical-insights" },
        ]}
      />
      <Overline>Clinical Insights</Overline>
      <h1 className="font-serif text-4xl md:text-6xl text-navy mt-3 tracking-tight leading-none">
        Decision points, distilled.
      </h1>
      <p className="text-lg text-stone2 mt-5 max-w-2xl">
        Brief, patient-facing insights on the moments in high-risk pregnancy where the reading of one result changes what happens next.
      </p>
      <div
        className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        data-testid="clinical-insights-gallery"
      >
        {APPROVED_CLINICAL_INSIGHTS.map((i) => (
          <Link
            key={i.slug}
            to={`/clinical-insights/${i.slug}`}
            data-testid={i.testid}
            className="group block bg-white border border-line p-6 hover:shadow-md transition-shadow"
          >
            <Overline>{i.variant}</Overline>
            <h3 className="font-serif text-2xl text-navy mt-2 leading-tight group-hover:underline underline-offset-4">
              {i.title}
            </h3>
            <p className="text-sm text-stone2 mt-3 leading-relaxed">
              {i.cardDescription}
            </p>
          </Link>
        ))}
      </div>
    </Section>
  );
}

export function ClinicalInsightDetail() {
  const { slug } = useParams();
  const meta = APPROVED_CLINICAL_INSIGHTS.find((i) => i.slug === slug);
  const Body = INSIGHT_BODIES[slug];

  if (!meta || !Body) {
    return (
      <Section>
        <SEO title="Clinical Insight not found" noindex />
        <p className="text-stone2">Clinical Insight not found.</p>
        <Link
          to="/clinical-insights"
          className="mono text-xs uppercase tracking-widest text-navy hover:underline inline-flex items-center gap-1.5 mt-4"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All Clinical Insights
        </Link>
      </Section>
    );
  }

  const canonical = buildCanonical(`/clinical-insights/${meta.slug}`);

  const medicalWebPageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    headline: meta.title,
    description: meta.metaDescription,
    url: canonical,
    inLanguage: "en",
    author: {
      "@type": "Person",
      name: meta.author,
    },
    about: {
      "@type": "MedicalCondition",
      name: meta.aboutCondition || meta.title,
    },
  };

  return (
    <Section>
      <SEO
        title={meta.seoTitle}
        description={meta.metaDescription}
        type="article"
        canonical={canonical}
        jsonLd={medicalWebPageSchema}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Clinical Insights", item: "/clinical-insights" },
          { name: meta.title, item: `/clinical-insights/${meta.slug}` },
        ]}
      />
      <div className="max-w-3xl mx-auto">
        <Link
          to="/clinical-insights"
          className="mono text-xs uppercase tracking-widest text-stone2 hover:text-navy inline-flex items-center gap-1.5"
          data-testid="insight-back-to-insights"
        >
          <ArrowLeft size={14} strokeWidth={1.5} /> All Clinical Insights
        </Link>
        <div className="mt-6">
          <Overline>{meta.variant}</Overline>
        </div>
        <h1
          className="font-serif text-4xl md:text-5xl text-navy mt-3 tracking-tight leading-[1.1]"
          data-testid="insight-heading"
        >
          {meta.title}
        </h1>

        <Body />
      </div>
    </Section>
  );
}
